import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, FileCheck, KeyRound, Eye } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PILLARS = [
  { icon: FileCheck, title: "Immutable Audit Trail", text: "Every allocation, correction, and approval is versioned and preserved. The public archive amends by addition, never by deletion — a conclusion drawn in January still reads as it was drawn in January." },
  { icon: KeyRound, title: "Sign-off Hierarchy", text: "Sensitive actions inside the private system require explicit approval. The same discipline governs this site: no research publishes without a logged owner approval event." },
  { icon: Eye, title: "Transparency by Record", text: "Trust is not requested, it is documented. Weekly Intelligence editions carry evidence cutoffs, confidence labels, source report IDs, and full version history." },
];

const NEVER_CROSSES = [
  "Exchange or provider credentials",
  "Treasury account balances or positions",
  "Private account APIs of any kind",
  "Execution routes or order surfaces",
  "The private Research Queue",
  "Strategy parameters or thresholds",
  "Tax information or entity details",
];

export default function Governance() {
  useEffect(() => {
    document.title = "Governance & Security | Hussar Treasury";
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + "/governance");
  }, []);

  return (
    <div data-testid="governance-page" className="pt-40">
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-20">
        <Reveal>
          <div className="metadata-label mb-6">Governance & Security</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-hussar-ink tracking-tight leading-[1.05] max-w-4xl">
            Governance you can audit. Boundaries you can verify.
          </h1>
          <p className="mt-8 max-w-2xl text-hussar-slate text-base sm:text-lg font-light leading-relaxed">
            Hussar Treasury operates as two separate estates: a private operating system and this public research
            record. The boundary between them is architectural, not contractual.
          </p>
        </Reveal>
      </section>

      {/* FIREWALL */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24">
        <Reveal>
          <div data-testid="firewall-section" className="relative border border-hussar-gold/25 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1637795065573-7a8132ec33fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Dark institutional facade"
              className="absolute inset-0 w-full h-full object-cover opacity-15"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-hussar-bg/70 to-hussar-bg" />
            <div className="relative p-10 sm:p-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Lock className="text-hussar-gold" size={22} strokeWidth={1.5} />
                  <div className="metadata-label">The Public / Private Firewall</div>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink leading-tight mb-6">
                  hussartreasury.com is public. hussarholdings.net is not.
                </h2>
                <p className="text-hussar-slate font-light leading-relaxed">
                  This site runs as its own application with its own database. It holds no connection to the
                  operational treasury system. The only thing that crosses the boundary is sanitized,
                  owner-approved research — in one direction, through one pipeline.
                </p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-muted mb-6">
                  What never crosses the boundary
                </div>
                <ul className="space-y-4" data-testid="firewall-list">
                  {NEVER_CROSSES.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-hussar-slate font-light">
                      <span className="text-hussar-gold mt-0.5">—</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PILLARS */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24 sm:pb-32">
        <Reveal>
          <div className="metadata-label mb-4">Governance Pillars</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight mb-16">How the record stays honest.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div data-testid={`pillar-${i}`} className="border border-white/5 bg-hussar-card p-10 h-full hover:border-hussar-gold/40 transition-colors duration-500">
                <p.icon className="text-hussar-gold mb-8" size={26} strokeWidth={1.25} />
                <h3 className="font-serif text-xl text-hussar-ink mb-4">{p.title}</h3>
                <p className="text-hussar-slate text-sm font-light leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-28 sm:pb-40 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight mb-10">Verify it in the archive.</h2>
          <Link to="/intelligence" data-testid="governance-to-archive-btn" className="btn-gold">
            Weekly Intelligence Archive <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
